import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calculator, Percent, Calendar, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const NegotiationModule = ({ 
  isOpen, 
  onClose, 
  totalDebt, 
  enableRefinancing,
  primaryColor,
  debtorName 
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [installments, setInstallments] = useState(3);
  const [promiseDate, setPromiseDate] = useState('');

  const installmentOptions = [
    { months: 3, interest: 0, label: '3 cuotas sin interés' },
    { months: 6, interest: 5, label: '6 cuotas (5% interés)' },
    { months: 12, interest: 10, label: '12 cuotas (10% interés)' }
  ];

  const discountOptions = [
    { percentage: 10, days: 0, label: 'Paga hoy y ahorra' },
    { percentage: 5, days: 7, label: 'Paga en 7 días' }
  ];

  const calculateInstallment = (option) => {
    const total = totalDebt * (1 + option.interest / 100);
    return (total / option.months).toFixed(2);
  };

  const calculateDiscount = (option) => {
    return (totalDebt * (1 - option.percentage / 100)).toFixed(2);
  };

  const handleSelectOption = (type, data) => {
    setSelectedOption({ type, data });
  };

  const handleConfirm = () => {
    if (selectedOption?.type === 'promise' && promiseDate) {
      toast.success(`Promesa de pago registrada para el ${new Date(promiseDate).toLocaleDateString('es-AR')}`);
      onClose();
    } else if (selectedOption) {
      toast.success('Opción seleccionada. Serás contactado para confirmar.');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="negotiation-module"
        >
          {/* Header */}
          <div 
            className="sticky top-0 px-6 py-4 border-b flex items-center justify-between"
            style={{backgroundColor: primaryColor + '10'}}
          >
            <div>
              <h3 className="text-xl font-bold" style={{fontFamily: 'Manrope', color: primaryColor}}>
                Opciones de Pago
              </h3>
              <p className="text-sm text-slate-600">
                Elige la opción que mejor se adapte a ti
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Deuda total */}
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <p className="text-sm text-slate-600 mb-1">Deuda Total</p>
              <p className="text-3xl font-bold text-slate-900">${totalDebt.toFixed(2)}</p>
            </div>

            {/* Descuentos por pago anticipado */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Percent className="w-5 h-5" style={{color: primaryColor}} />
                <h4 className="font-semibold text-slate-900">Descuentos Especiales</h4>
              </div>
              <div className="grid gap-3">
                {discountOptions.map((option, idx) => (
                  <Card
                    key={idx}
                    className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                      selectedOption?.type === 'discount' && selectedOption?.data === option
                        ? 'ring-2 shadow-lg'
                        : 'hover:border-slate-300'
                    }`}
                    style={{
                      borderColor: selectedOption?.type === 'discount' && selectedOption?.data === option ? primaryColor : undefined
                    }}
                    onClick={() => handleSelectOption('discount', option)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">{option.label}</p>
                        <p className="text-sm text-slate-600">
                          {option.percentage}% de descuento
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold" style={{color: primaryColor}}>
                          ${calculateDiscount(option)}
                        </p>
                        <p className="text-xs text-emerald-600 font-medium">
                          Ahorras ${(totalDebt - calculateDiscount(option)).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Planes de cuotas */}
            {enableRefinancing && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Calculator className="w-5 h-5" style={{color: primaryColor}} />
                  <h4 className="font-semibold text-slate-900">Planes de Cuotas</h4>
                </div>
                <div className="grid gap-3">
                  {installmentOptions.map((option, idx) => (
                    <Card
                      key={idx}
                      className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                        selectedOption?.type === 'installment' && selectedOption?.data === option
                          ? 'ring-2 shadow-lg'
                          : 'hover:border-slate-300'
                      }`}
                      style={{
                        borderColor: selectedOption?.type === 'installment' && selectedOption?.data === option ? primaryColor : undefined
                      }}
                      onClick={() => handleSelectOption('installment', option)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-slate-900">{option.label}</p>
                          <p className="text-sm text-slate-600">
                            {option.interest > 0 ? `Interés: ${option.interest}%` : 'Sin interés'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold" style={{color: primaryColor}}>
                            ${calculateInstallment(option)}
                          </p>
                          <p className="text-xs text-slate-600">por mes</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Promesa de pago */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-5 h-5" style={{color: primaryColor}} />
                <h4 className="font-semibold text-slate-900">No puedo pagar ahora</h4>
              </div>
              <Card className="p-4">
                <p className="text-sm text-slate-600 mb-3">
                  ¿Cuándo podrías realizar el pago?
                </p>
                <Input
                  type="date"
                  value={promiseDate}
                  onChange={(e) => {
                    setPromiseDate(e.target.value);
                    handleSelectOption('promise', { date: e.target.value });
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  className="mb-2"
                />
                <p className="text-xs text-slate-500">
                  Te recordaremos en la fecha indicada
                </p>
              </Card>
            </div>

            {/* Botones de acción */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={!selectedOption}
                className="flex-1 text-white"
                style={{backgroundColor: selectedOption ? primaryColor : undefined}}
              >
                Confirmar Opción
              </Button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                ℹ️ Un representante se pondrá en contacto para confirmar tu elección
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NegotiationModule;
